import { MRT_ColumnDef } from "material-react-table";
import { FC, useMemo, useState } from "react";

import { CollapsibleCard } from "@/components/elements/CollapsibleCard";
import { EMPTY_STR } from "@/constants/string";
import { SegmentedControl } from "@/modules/irohana-components/elements/SegmentedControl";
import { Table } from "@/modules/irohana-components/elements/Table";
import { useTable } from "@/modules/irohana-components/hooks/useTable";
import {
  PRICE_DIMENSION,
  PRICE_DIMENSION_LABEL,
  TPriceDimension,
} from "@/features/organization/product/constants/productEnums";
import { TProductDetail, TTypePrice } from "@/api/services/main/organization/product/dtos/product.dto";
import { formatUnitPrice, usesOtherDimension } from "@/features/organization/product/utils/productUtils";
import { useProductMasters } from "@/features/organization/product/hooks/useProductMasters";

type TProductTypePriceViewProps = {
  product: TProductDetail;
};

/** 種別単価設定 — SegmentedControl + read-only price table (商品詳細). */
export const ProductTypePriceView: FC<TProductTypePriceViewProps> = ({ product }) => {
  const { getNationalityName, getIndustryName } = useProductMasters();

  const resolveValueLabel = (row: TTypePrice): string => {
    switch (row.dimension) {
      case PRICE_DIMENSION.NATIONALITY:
        return row.nationalityId == null ? "全ての国籍" : getNationalityName(row.nationalityId);
      case PRICE_DIMENSION.FIELD:
        return row.fieldId == null ? "全ての分野" : getIndustryName(row.fieldId);
      case PRICE_DIMENSION.TIER:
        return row.variantValue == null
          ? row.isDefault
            ? "全てのティア" : EMPTY_STR.TEXT
          : row.variantValue;
      default:
        return row.variantValue || EMPTY_STR.TEXT;
    }
  };

  // 人材紹介料 shows その他 instead of ティア (C3). Otherwise ティア is shown only when the product is
  // a tier target (isTierDiscountTarget = true, cloned from master_default_products); when false,
  // just 国籍 / 分野 (confirmed 2026-08-24).
  const dimensions: TPriceDimension[] = usesOtherDimension(product.name)
    ? [PRICE_DIMENSION.NATIONALITY, PRICE_DIMENSION.FIELD, PRICE_DIMENSION.OTHER]
    : product.isTierDiscountTarget
    ? [PRICE_DIMENSION.NATIONALITY, PRICE_DIMENSION.FIELD, PRICE_DIMENSION.TIER]
    : [PRICE_DIMENSION.NATIONALITY, PRICE_DIMENSION.FIELD];

  const [active, setActive] = useState<TPriceDimension>(dimensions[0]);
  const rows = useMemo(() => product.typePrices.filter((row) => row.dimension === active), [product.typePrices, active]);
  const columns = useMemo<MRT_ColumnDef<TTypePrice>[]>(
    () => [
      { id: "value", header: PRICE_DIMENSION_LABEL[active], Cell: ({ row }) => resolveValueLabel(row.original), size: 532 },
      {
        id: "unitPrice",
        header: "単価",
        muiTableBodyCellProps: { align: "right" },
        Cell: ({ row }) => formatUnitPrice(row.original.unitPrice, product.unit),
      },
    ],
    [active, product.unit],
  );

  const { table } = useTable({ rows, columns });

  return (
    <CollapsibleCard
      title="種別単価設定"
      description="国籍、分野、ティアに各単価を設定できます。未設定の項目は基本単価となります。"
      showBorder={false}
      headerAction={
        <SegmentedControl
          value={active}
          onChange={setActive}
          options={dimensions.map((d) => ({ value: d, label: PRICE_DIMENSION_LABEL[d] }))}
        />
      }
    >
      <Table table={table} hiddenAddBtn />
    </CollapsibleCard>
  );
};
